import { Stack } from '@superdispatch/ui';
import { Box, TextBox } from '@superdispatch/ui-lab';
import { useEffect, useId, useRef, useState } from 'react';
import styled from 'styled-components';
import unlockBackgroundImage from '../../../assets/unlock_bg.png';
import { createHubSpotForm } from '../../../services/HubSpotService';

const HUBSPOT_PORTAL_ID = '44142879';
const HUBSPOT_FORM_ID = '49336146-e4e7-4591-9c96-cc8b3f702d42';
const HUBSPOT_REGION = 'na1';

const Card = styled.div`
  width: min(100%, 520px);
  padding: 24px;

  border-radius: 16px;
  border: 1px solid #353d4d;
  background: url(${unlockBackgroundImage});
  background-size: cover;
`;

const FormContainer = styled.div`
  min-height: 360px;
  color: #ffffff;

  .hs-form-frame,
  .hbspt-form {
    width: 100%;
  }

  .hs-form,
  .hs-form fieldset {
    width: 100%;
    max-width: none;
    margin: 0;
    padding: 0;
    border: 0;
  }

  .hs-form fieldset.form-columns-1 .hs-form-field,
  .hs-form .hs_email,
  .hs-form .hs-fieldtype-select {
    width: 100% !important;
    max-width: none;
  }

  .hs-form fieldset.form-columns-1 .input,
  .hs-form .hs_email .input,
  .hs-form .hs-fieldtype-select .input {
    margin-right: 8px !important;
  }

  .hs-form-field,
  .legal-consent-container,
  .hs-richtext {
    margin-bottom: 16px;
  }

  .hs-form-field > label,
  .hs-richtext,
  .legal-consent-container,
  .hs-error-msgs,
  .submitted-message,
  .inputs-list,
  .inputs-list label,
  .hs-form-booleancheckbox-display,
  .hs-form-booleancheckbox-display span,
  .hs-form-booleancheckbox-display p,
  .legal-consent-container p,
  .legal-consent-container span,
  .submitted-message p {
    color: #ffffff;
  }

  .inputs-list {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .inputs-list > li {
    margin: 0;
  }

  .hs-input:not([type='checkbox']):not([type='radio']),
  .hs-form select.hs-input {
    width: 100%;
    min-height: 44px;
    padding: 10px 12px;
    box-sizing: border-box;

    color: #ffffff;
    border: 1px solid #586174;
    border-radius: 8px;
    background: rgba(10, 18, 33, 0.7);
  }

  .hs-form select.hs-input {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    padding-right: 44px;
    background-image: none;
  }

  .hs-input::placeholder {
    color: #b6bdc9;
  }

  .hs-fieldtype-select .input {
    position: relative;
  }

  .hs-fieldtype-select .input::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 18px;
    width: 12px;
    height: 8px;
    pointer-events: none;
    transform: translateY(-50%);
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'><path d='M1 1.5L6 6.5L11 1.5' stroke='%23FFFFFF' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>");
    background-repeat: no-repeat;
    background-size: 12px 8px;
  }

  .hs-form-booleancheckbox-display {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }

  .hs-form-booleancheckbox-display input {
    width: 16px;
    height: 16px;
    min-height: 16px;
    padding: 0;
    margin-top: 4px;
    accent-color: #fe4a49;
  }

  .hs-form-booleancheckbox-display span {
    display: inline;
    line-height: 1.5;
  }

  .hs-error-msgs {
    margin: 8px 0 0;
    padding-left: 20px;
  }

  .legal-consent-container a,
  .hs-richtext a,
  .submitted-message a {
    color: #9ec7ff;
  }

  .grecaptcha-badge,
  .grecaptcha-logo,
  .recaptcha-checkbox-border {
    filter: none;
  }

  .hs-button {
    min-height: 44px;
    padding: 10px 20px;

    color: #ffffff;
    font-weight: 600;
    border: 0;
    border-radius: 8px;
    background: #fe4a49;
    cursor: pointer;
  }

  .submitted-message {
    line-height: 1.6;
  }
`;

const FallbackLink = styled.a`
  color: inherit;
`;

const HeadingText = styled(TextBox)`
  color: #ffffff;
`;

interface Props {
  className?: string;
}

export function ContactSalesForm({ className }: Props) {
  const formTargetId = `hubspot-form-${useId().replace(/:/g, '')}`;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    if (containerRef.current) {
      containerRef.current.innerHTML = '';
    }

    void createHubSpotForm({
      portalId: HUBSPOT_PORTAL_ID,
      formId: HUBSPOT_FORM_ID,
      region: HUBSPOT_REGION,
      target: `#${formTargetId}`,
    }).catch(() => {
      if (!isCancelled) {
        setLoadError(true);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [formTargetId]);

  return (
    <Card className={className}>
      <Stack space="medium" align="center">
        <HeadingText align="center" variant="heading-3">
          Talk to Us to Get More Pricing Insights
        </HeadingText>

        <Box width="100%">
          {loadError ? (
            <TextBox align="center" color="white">
              We couldn&apos;t load the form. Please try again or use{' '}
              <FallbackLink
                href="https://superdispatch.com/shipper-request-demo/"
                target="_blank"
                rel="noreferrer"
              >
                this request form
              </FallbackLink>
              .
            </TextBox>
          ) : (
            <FormContainer
              id={formTargetId}
              ref={containerRef}
              aria-label="Contact sales form"
            />
          )}
        </Box>
      </Stack>
    </Card>
  );
}
